import { orderBy } from "lodash";
import type { DictionaryItem } from "@sa/rep-mdm";
import { DimensionItemDTO, IMeasureItemDTO, IFieldItemDTO } from "./dimensionItems.types";

export function createModelItems<T extends number | string>(dictionaryItems: DictionaryItem[], lcid?: number): DimensionItemDTO<T>[] {
  const finalItems = dictionaryItems.map<DimensionItemDTO<T>>((it) => {
    const id = getFirstValue<number | string>(it, ["id", "identifier"], lcid);
    const name = getFirstValue(it, ["name"], lcid);
    const parent = getFirstValue(it, ["parent", "parent_id", "parentId"], lcid);
    const order = getFirstValue(it, ["order"], lcid);
    const frequencyId = getFirstValue(it, ["frequency", "frequency_id", "frequencyId"], lcid);

    const item = {
      id,
      name,
      parent,
      order,
      frequencyId,
    } as DimensionItemDTO<T>;

    return {
      ...item,
      getValue,
    } as DimensionItemDTO<T>;
  });
  return orderBy(finalItems, (it) => it.order);
}

function getFirstValue<T>(dictionaryItem: DictionaryItem, attributes: string[], lcid?: number): T {
  let value: unknown;
  for (const attribute of attributes) {
    const metadata = dictionaryItem.metadata.get(attribute);
    if (metadata) {
      if (metadata.isLocalized) {
        value = dictionaryItem.getLocalizedValue(attribute, lcid ?? 9);
      } else {
        value = dictionaryItem.getSingleValue(attribute);
      }
      if (typeof value === "object") {
        value = value?.[lcid ?? 9];
      }
      break;
    }
  }
  return value as T;
}

export function createConfigurationItems<T extends number | string>(
  measures: { id: string; name: string }[],
  fields: { identifier: string; name: string }[]
) {
  const finalMeasures = measures.map(
    (measure) =>
      ({
        id: `${measure.id}`,
        name: measure.name,
        isMeasure: true,
        getValue,
      } as IMeasureItemDTO<T>)
  );

  const finalFields = fields.map(
    (field) =>
      ({
        id: `${field.identifier}`,
        name: field.name,
        isField: true,
        getValue,
      } as IFieldItemDTO<T>)
  );

  return [...finalMeasures, ...finalFields] as DimensionItemDTO<T>[];
}

function getValue<T>(this: DimensionItemDTO<T>, field: keyof DimensionItemDTO<T>) {
  if (field === "getValue") {
    return undefined;
  }
  return this[field];
}
